"use client";

/**
 * 2026-05-05 — @-mention autocomplete (founder ask 2026-05-04 6-item
 * stack: "WHEN @not loading the topic, the market or the user to tag").
 *
 * Detects `@<token>` being typed inside a textarea. Once the token has
 * 1+ chars it fans out a parallel search across users + markets +
 * topics and renders a single dropdown with grouped results. Selecting
 * a row replaces `@<token>` with `@<resolved-slug>` (markets +
 * topics) or `@<username>` and appends a trailing space.
 *
 * Resolution targets:
 *   - users     → `@<username>`        link via /u/<username>
 *   - markets   → `@<market-slug>`     link via /markets/<slug>
 *   - topics    → `@<topic-slug>`      link via /topic/<slug>
 *
 * The MentionParser renderer already handles users + markets; topics
 * are not resolved client-side yet — for now `@<topic-slug>` will
 * render as a plain `@token` text on the post. Backend post-content
 * preprocessing will catch topic resolution in a follow-up; that's a
 * server-side change that's safe to defer because the dropdown
 * inserts the same `@<slug>` shape regardless.
 *
 * Closed-beta scope:
 *   - Anchored below the textarea (full-width). Caret-anchored
 *     positioning with proper line-height awareness is a follow-up
 *     once the cohort grows; for closed-beta the visible-on-screen
 *     dropdown is the priority.
 *   - Keyboard nav: Up/Down/Enter/Esc/Tab. Mouse: click-to-pick.
 *   - 200ms debounce. Network failures keep the previous suggestion
 *     list so the UX doesn't flicker on every flaky request.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  api,
  type MarketListItem,
  type TopicRecord,
  type UserSearchHit,
} from "@/lib/api";

type MentionKind = "user" | "market" | "topic";

interface Suggestion {
  kind: MentionKind;
  // The raw insertion target — drop into the textarea verbatim
  // (without the leading `@`, which the helper preserves).
  value: string;
  // Display fields for the dropdown row.
  primary: string;
  secondary?: string;
}

interface TriggerCtx {
  // Inclusive index of the `@` that opened the trigger.
  startIdx: number;
  // Exclusive index of the caret position when the trigger was
  // detected — we replace [startIdx, endIdx) on selection.
  endIdx: number;
  // The token between `@` and the caret (no leading `@`).
  token: string;
}

const MAX_PER_KIND = 5;
const DEBOUNCE_MS = 200;
// Token shape that's worth searching for. Lower bound prevents an
// expensive 3-way fanout on a bare `@` followed by whitespace.
const TOKEN_REGEX = /^[a-z0-9_-]{1,32}$/i;

export interface UseMentionAutocompleteHandle {
  open: boolean;
  suggestions: Suggestion[];
  selectedIdx: number;
  loading: boolean;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  pick: (idx: number) => void;
  close: () => void;
}

export function useMentionAutocomplete(
  textareaRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>,
  value: string,
  setValue: (v: string) => void,
): UseMentionAutocompleteHandle {
  const [trigger, setTrigger] = useState<TriggerCtx | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  // Derive trigger from value + caret position whenever the textarea
  // changes. We can't read selectionStart from `value` alone — the
  // host component re-renders on every keystroke and we read the ref
  // after render commits.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? value.length;
    const next = detectTrigger(value, caret);
    setTrigger(next);
    if (!next) {
      setSuggestions([]);
      setSelectedIdx(0);
    }
    // value & caret changes both come through the host's onChange/
    // onKeyUp — we don't need a separate listener.
  }, [value, textareaRef]);

  // Debounced fetch on trigger change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!trigger || trigger.token.length === 0) {
      setSuggestions([]);
      return;
    }
    if (!TOKEN_REGEX.test(trigger.token)) {
      setSuggestions([]);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const [users, markets, topics] = await Promise.all([
          api
            .searchUsers(trigger.token, MAX_PER_KIND)
            .catch(() => ({ users: [] as UserSearchHit[] })),
          api
            .listMarkets({ q: trigger.token, limit: MAX_PER_KIND })
            .catch(() => ({ markets: [] as MarketListItem[], nextCursor: null })),
          api
            .searchTopics({ q: trigger.token, limit: MAX_PER_KIND })
            .catch(() => ({ topics: [] as TopicRecord[] })),
        ]);
        // Ignore stale responses — the user typed past this token.
        if (reqId !== reqIdRef.current) return;
        const merged: Suggestion[] = [
          ...users.users.map<Suggestion>((u) => ({
            kind: "user",
            value: u.username,
            primary: u.displayName ?? `@${u.username}`,
            secondary: `@${u.username}`,
          })),
          ...markets.markets.map<Suggestion>((m) => ({
            kind: "market",
            value: m.slug,
            primary: typeof m.title === "string" ? m.title : (m.title.en ?? m.slug),
            secondary: `market · ${m.slug}`,
          })),
          ...topics.topics.map<Suggestion>((t) => ({
            kind: "topic",
            value: t.slug,
            primary: t.name,
            secondary: `topic · ${t.slug}`,
          })),
        ];
        setSuggestions(merged);
        setSelectedIdx(0);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [trigger]);

  const close = useCallback(() => {
    setTrigger(null);
    setSuggestions([]);
    setSelectedIdx(0);
  }, []);

  const pick = useCallback(
    (idx: number) => {
      if (!trigger) return;
      const s = suggestions[idx];
      if (!s) return;
      const before = value.slice(0, trigger.startIdx);
      const after = value.slice(trigger.endIdx);
      const sep = after.startsWith(" ") ? "" : " ";
      const next = `${before}@${s.value}${sep}${after}`;
      setValue(next);
      // Restore caret after the inserted token + trailing space.
      const newCaret = before.length + 1 + s.value.length + sep.length;
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(newCaret, newCaret);
        }
      });
      close();
    },
    [trigger, suggestions, value, setValue, close, textareaRef],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!trigger || suggestions.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx((i) => (i + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx(
          (i) => (i - 1 + suggestions.length) % suggestions.length,
        );
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(selectedIdx);
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    },
    [trigger, suggestions, selectedIdx, pick, close],
  );

  return {
    open: !!trigger && suggestions.length > 0,
    suggestions,
    selectedIdx,
    loading,
    onKeyDown,
    pick,
    close,
  };
}

// Walk backwards from `caret` looking for the most recent `@` that
// starts a mention token. Boundary rules:
//   - The `@` must be preceded by start-of-text or whitespace
//   - The token between `@` and the caret must be alphanumeric +
//     `_` / `-` only (whitespace or punctuation closes the trigger)
function detectTrigger(value: string, caret: number): TriggerCtx | null {
  if (caret <= 0) return null;
  // Walk back at most 64 chars — longer tokens aren't real mentions.
  const lo = Math.max(0, caret - 64);
  for (let i = caret - 1; i >= lo; i--) {
    const ch = value[i];
    if (ch === "@") {
      const before = i > 0 ? value[i - 1] : "";
      if (before !== "" && !/\s/.test(before)) return null;
      const token = value.slice(i + 1, caret);
      // Reject if a whitespace got into the token (closed trigger).
      if (/\s/.test(token)) return null;
      return { startIdx: i, endIdx: caret, token };
    }
    if (/\s/.test(ch)) return null;
  }
  return null;
}

interface MentionAutocompleteListProps {
  handle: UseMentionAutocompleteHandle;
  // Anchor element (the textarea / input). The dropdown anchors below
  // it via document flow — no portal needed because the dropdown is
  // rendered directly inside the composer's container which has no
  // backdrop-filter ancestor.
}

export const MentionAutocompleteList = forwardRef<
  HTMLUListElement,
  MentionAutocompleteListProps
>(function MentionAutocompleteList({ handle }, ref) {
  const { open, suggestions, selectedIdx, pick } = handle;
  if (!open) return null;
  return (
    <ul
      ref={ref}
      role="listbox"
      aria-label="Mention suggestions"
      className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-auto rounded-md border bg-[var(--color-surface)] shadow-lg"
      style={{ borderColor: "var(--color-border)" }}
    >
      {suggestions.map((s, i) => {
        const active = i === selectedIdx;
        return (
          <li key={`${s.kind}:${s.value}`}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              onMouseDown={(e) => {
                // mouseDown so we beat the textarea's onBlur which
                // would otherwise close the dropdown before click
                // fires.
                e.preventDefault();
                pick(i);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors"
              style={{
                background: active
                  ? "color-mix(in srgb, var(--color-brand-tint) 70%, var(--color-surface))"
                  : "transparent",
                color: "var(--color-text)",
              }}
            >
              <span
                className="rounded-[3px] border px-1 text-[9px] font-bold uppercase tracking-[0.4px]"
                style={{
                  borderColor: "var(--color-border)",
                  color: "var(--color-text3)",
                }}
              >
                {s.kind}
              </span>
              <span className="truncate font-bold">{s.primary}</span>
              {s.secondary && (
                <span className="ml-auto truncate text-[11px] text-[var(--color-text3)]">
                  {s.secondary}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
});

export const __test = { detectTrigger };
